import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTE } from "../routes";
import { displayError } from "../utils/error-handling";
import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { STORAGE_KEYS } from "../utils/constants";

const AxiosInterceptor = ({ children }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [message, setMessage] = useState("");
  const in401Error = useRef(false);

  const setError = useCallback(
    (val) => {
      if (val !== message) {
        setMessage(val);
      }
    },
    [message],
  );

  useEffect(() => {
    if (message) {
      displayError(message);
      setMessage("");
    }
  }, [message]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.config?.skipGlobalErrorHandler) {
          return Promise.reject(error);
        }

        const statusCode = error?.response?.status;
        const errorResponseData = error?.response?.data;

        if ((statusCode === 401 || statusCode === 403) && !in401Error.current) {
          in401Error.current = true;

          if (!message) {
            setError(
              errorResponseData?.detail
                ? errorResponseData.detail
                : "Authentication failed.",
            );
          }

          const raw = (location.pathname + location.search).replace(/^\//, "");
          if (!window.sessionStorage.getItem(STORAGE_KEYS.pendingReturnUrl)) {
            window.sessionStorage.setItem(STORAGE_KEYS.pendingReturnUrl, raw);
          }

          if (location.pathname !== ROUTE.LOGIN) {
            const loginWithReturn = `${
              ROUTE.LOGIN
            }?returnurl=${encodeURIComponent(raw)}`;
            logout(loginWithReturn);
          }

          setTimeout(() => {
            in401Error.current = false;
          }, 1000);

          return Promise.reject(error);
        } else if (statusCode === 404) {
          navigate(ROUTE.NOT_FOUND);
        } else if (statusCode === 500 || statusCode === 503) {
          navigate(ROUTE.SERVER_ERROR);
        }

        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [location.pathname, location.search, logout, navigate, setError, message]);

  return children;
};

export { axios, AxiosInterceptor };
